import React, { memo } from "react";

import { transformDateToLocalDateString } from "helpers";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";
import {
  countTires,
  formatTireDimension,
  TIRE_SET_INTRO_SAMPLE,
} from "ui/modules/fleet/tires/pages/TireSetEditCard/components/TireSetIntro/consts";

import { getRelocateRoute } from "../consts";
import { TireSetRelocateSceneProps } from "../types";

/** Korak "Skladište": ruta od starog do novog skladišta i set koji seli. */
const RelocateWarehouseScene: React.FC<TireSetRelocateSceneProps> = (props) => {
  const { currentSet, formValues } = props;
  const { from, to } = getRelocateRoute(props);
  const code = currentSet.code || TIRE_SET_INTRO_SAMPLE.code;
  const dimension = formatTireDimension(currentSet.tires[0]) || TIRE_SET_INTRO_SAMPLE.dimension;
  const quantity = countTires(currentSet.tires) || TIRE_SET_INTRO_SAMPLE.quantity;
  const date = transformDateToLocalDateString(formValues.transferDate ?? new Date());

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Premještaj seta</div>
          <div className="text-sm font-bold text-slate-800 truncate">{code}</div>

          {/* Ruta: staro skladište gore, novo dolje, isprekidana crta između. */}
          <div className="mt-4 flex gap-3">
            <div className="flex flex-col items-center pt-3">
              <span className="w-3 h-3 rounded-full border-2 border-slate-400 bg-white" />
              <span className="flex-1 w-px my-1 border-l-2 border-dashed border-slate-300" />
              <span className="w-3 h-3 rounded-full bg-blue-600 ring-4 ring-blue-100" />
            </div>
            <div className="flex-1 min-w-0 space-y-6">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-md bg-slate-100 flex-shrink-0">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 21V8l9-5 9 5v13M7 21v-8h10v8M7 17h10" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <div className="text-[0.6rem] text-slate-400">Sada</div>
                  <div className="text-[0.75rem] font-semibold text-slate-500 truncate">{from}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-md bg-blue-50 flex-shrink-0">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 21V8l9-5 9 5v13M7 21v-8h10v8M7 17h10" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <div className="text-[0.6rem] text-blue-500 font-semibold">Novo</div>
                  <div className="text-sm font-bold text-slate-800 truncate">{to}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-[0.7rem]">
            <span className="text-slate-500">Datum premještaja</span>
            <span className="font-semibold text-slate-800">{date}</span>
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          {/* Slika ima bijelu podlogu kao kartica, pa bez okvira. */}
          <div className="h-[5rem] overflow-hidden">
            <img src={TIRE_SET_INTRO_SAMPLE.image} alt="" className="w-full h-full object-contain scale-[1.35]" />
          </div>
          <div className="mt-3 text-xs font-bold text-slate-800 truncate">{code}</div>
          <div className="text-[0.65rem] text-slate-400">
            {dimension} · {quantity} kom
          </div>
          <div className="mt-2 inline-flex px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-amber-100 text-amber-700">
            Seli
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Uvijek znate gdje su gume"
          subtitle="Lokacija seta se prati automatski"
          iconClassName="bg-emerald-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 21s7-7.75 7-13a7 7 0 00-14 0c0 5.25 7 13 7 13z" />
              <circle cx="12" cy="8" r="2.5" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(RelocateWarehouseScene);
